/**
 * Normalize a report field to display text. Strings are trimmed, finite numbers are stringified,
 * and anything else (null, undefined, objects) becomes `""` so it falls through to the next
 * candidate.
 */
const toText = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
};

/**
 * Pick a readable label for a saved report: its `name`, then `query`, then `url`, then the
 * report-type fallback. Chains with `||` (not `??`) so empty or whitespace-only values fall through
 * too.
 *
 * @example
 *   ```ts
 *   reportName({ name: "  ", query: "best hotels in Goa" }, "AI Perception Report");
 *   // => "best hotels in Goa"
 *   reportName({ name: "" }, "AI Perception Report");
 *   // => "AI Perception Report"
 *   ```;
 *
 * @param report - The report record as returned by the list API.
 * @param fallbackName - Label used when the report has no usable name, query, or url.
 * @returns The first non-empty candidate, or `fallbackName`.
 */
export const reportName = (report: Record<string, unknown>, fallbackName: string): string =>
  toText(report.name) || toText(report.query) || toText(report.url) || fallbackName;
